import type { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Tooltip } from '@material-ui/core';
import { confidenceIndice } from '../static-data';

export interface IpopStep {
  /** Short letter shown under the bar (U/A/S/F). */
  key: string;
  /** Full name, shown in the tooltip. */
  label: string;
  /** Confidence level 0-5 driving the fill + color. */
  level: number;
  /** Route prefix used to highlight the active step. */
  pathPrefix: string;
  /** Where clicking the step navigates. */
  href: string;
}

/**
 * IPOP progress stepper (from the "Ipop Tools — Improved" design): one bar per
 * review, filled to its confidence level and colored with the shared
 * confidence palette. Compact enough to stay visible in the collapsed sidebar.
 */
export const IpopStepper: FC<{ steps: IpopStep[] }> = ({ steps }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (!steps.length) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', gap: '6px', px: 0.5, pb: 1.5 }}>
      {steps.map((s) => {
        const lvl = Math.max(0, Math.min(5, s.level || 0));
        const pct = (lvl / 5) * 100;
        // lvl can be fractional (average of sub-confidences) — round for the
        // discrete confidence color palette lookup.
        const color = lvl > 0 ? confidenceIndice[Math.round(lvl)]?.['color'] : undefined;
        const active = pathname.startsWith(s.pathPrefix);
        return (
          <Tooltip key={s.key} title={`${s.label} · ${Math.round(pct)}%`} arrow placement="bottom">
            <Box
              onClick={() => navigate(s.href)}
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', cursor: 'pointer' }}
            >
              <Box sx={{ height: 4, borderRadius: 999, bgcolor: 'divider', overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${pct}%`,
                    bgcolor: color || 'transparent',
                    borderRadius: 999,
                    transition: 'width .4s ease',
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  color: active ? 'text.primary' : 'text.secondary',
                }}
              >
                {s.key}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};
